import { ArrowDownIcon, ArrowUpIcon, DeleteIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Table,
    Tbody,
    Td,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
    createBsTestingType,
    deleteBsTestingType,
    getAllBsTestingTypes,
    restoreBsTestingType,
    updateBsTestingType
} from '../../BSApi';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission, useTableColors } from '../../Utils';

const emptyForm = () => ({
    name: '',
    code: '',
    description: '',
    sortOrder: 0,
    isActive: true
});

const BSSTestingTypesPage = () => {
    const coolors = useTableColors();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newItem, setNewItem] = useState(emptyForm());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [sortField, setSortField] = useState('sortOrder');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => { loadItems(); }, [showDeleted]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllBsTestingTypes(showDeleted);
            if (response.success) setItems(Array.isArray(response.data) ? response.data : []);
            else setError(response.error || 'Failed to load testing types');
        } catch {
            setError('Failed to load testing types');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return [...items]
            .filter((item) => !term || [item.name, item.code, item.description].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)))
            .sort((a, b) => {
                const aValue = a?.[sortField] ?? '';
                const bValue = b?.[sortField] ?? '';
                if (typeof aValue === 'number' && typeof bValue === 'number') return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
                const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
                return sortOrder === 'asc' ? result : -result;
            });
    }, [items, searchTerm, sortField, sortOrder]);

    const toggleSort = (field) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
    };

    const handleChange = (setter, field, value) => setter((prev) => ({ ...prev, [field]: value }));
    const openEdit = (item) => { setSelectedItem({ ...item }); onOpen(); };
    const openDelete = (item) => { setSelectedItem(item); onDeleteOpen(); };

    const handleCreate = async () => {
        const payload = { ...newItem, sortOrder: Number(newItem.sortOrder) || 0 };
        const response = await createBsTestingType(payload);
        if (response.success) {
            setNewItem(emptyForm());
            onCreateClose();
            loadItems();
        } else alert(response.error || 'Failed to create testing type');
    };

    const handleSave = async () => {
        const payload = { ...selectedItem, sortOrder: Number(selectedItem.sortOrder) || 0 };
        const response = await updateBsTestingType(selectedItem._id, payload);
        if (response.success) {
            onClose();
            loadItems();
        } else alert(response.error || 'Failed to update testing type');
    };

    const handleDelete = async () => {
        const response = await deleteBsTestingType(selectedItem._id);
        if (response.success) {
            onDeleteClose();
            loadItems();
        } else alert(response.error || 'Failed to delete testing type');
    };

    const handleRestore = async (id) => {
        const response = await restoreBsTestingType(id);
        if (response.success) loadItems();
        else alert(response.error || 'Failed to restore testing type');
    };

    if (loading) return <Center mt="20%"><Loading /></Center>;

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                {!error ? (
                    <>
                        <TitleHeader title="BSS Testing Types" buttonText={hasPermission('createBSSTestingType') ? 'Create New Testing Type' : undefined} onClick={hasPermission('createBSSTestingType') ? onCreateOpen : undefined} />
                        <HStack spacing={4} mb="4" align="end">
                            <FormControl>
                                <FormLabel>Search</FormLabel>
                                <Input placeholder="Search by name, code, description" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </FormControl>
                            <Checkbox isChecked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>Show deleted</Checkbox>
                        </HStack>

                        {filteredItems.length ? (
                            <Table variant="simple" size="sm">
                                <Thead bg={coolors.theadBg}>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('name')}>Name {sortField === 'name' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th>Code</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('sortOrder')}>Sort {sortField === 'sortOrder' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th>Status</Th>
                                        <Th textAlign="right">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredItems.map((item, index) => (
                                        <Tr key={item._id} borderBottom={`2px solid ${coolors.borderColor}`} bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd} color={coolors.textColor} _hover={{ bg: coolors.rowHoverBg, color: 'white' }}>
                                            <Td><PopoverId place="BSS Testing Types" index={index + 1} content={item._id} /></Td>
                                            <Td>{item.name}</Td>
                                            <Td>{item.code || '-'}</Td>
                                            <Td>{item.sortOrder ?? 0}</Td>
                                            <Td><Badge colorScheme={item.deletedAt ? 'red' : item.isActive ? 'green' : 'yellow'}>{item.deletedAt ? 'Deleted' : item.isActive ? 'Active' : 'Inactive'}</Badge></Td>
                                            <Td textAlign="right">
                                                <HStack justifyContent="flex-end" spacing={2}>
                                                    {!item.deletedAt && hasPermission('updateBSSTestingType') && <Button size="xs" colorScheme="blue" onClick={() => openEdit(item)}><EditIcon /></Button>}
                                                    {!item.deletedAt && hasPermission('deleteBSSTestingType') && <Button size="xs" colorScheme="red" onClick={() => openDelete(item)}><DeleteIcon /></Button>}
                                                    {item.deletedAt && hasPermission('restoreBSSTestingType') && <Button size="xs" colorScheme="green" onClick={() => handleRestore(item._id)}><RepeatIcon /></Button>}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        ) : <AccessDenied msg="No BSS testing types found!" icon="info" />}
                    </>
                ) : <AccessDenied msg={error} />}
            </VStack>

            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirm Deletion</ModalHeader>
                    <ModalBody>Are you sure you want to delete <strong>{selectedItem?.name}</strong>?</ModalBody>
                    <ModalFooter>
                        <Button colorScheme="red" mr={3} onClick={handleDelete}>Delete</Button>
                        <Button onClick={onDeleteClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Testing Type</ModalHeader>
                    <ModalBody>
                        {selectedItem && (
                            <VStack spacing={4}>
                                <FormControl><FormLabel>Name</FormLabel><Input value={selectedItem.name || ''} onChange={(e) => handleChange(setSelectedItem, 'name', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Code</FormLabel><Input value={selectedItem.code || ''} onChange={(e) => handleChange(setSelectedItem, 'code', e.target.value.toUpperCase())} /></FormControl>
                                <FormControl><FormLabel>Description</FormLabel><Textarea value={selectedItem.description || ''} onChange={(e) => handleChange(setSelectedItem, 'description', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Sort Order</FormLabel><Input type="number" value={selectedItem.sortOrder ?? 0} onChange={(e) => handleChange(setSelectedItem, 'sortOrder', e.target.value)} /></FormControl>
                                <Checkbox isChecked={!!selectedItem.isActive} onChange={(e) => handleChange(setSelectedItem, 'isActive', e.target.checked)}>Active</Checkbox>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSave}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" isOpen={isCreateOpen} onClose={onCreateClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create Testing Type</ModalHeader>
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl><FormLabel>Name</FormLabel><Input value={newItem.name} onChange={(e) => handleChange(setNewItem, 'name', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Code</FormLabel><Input value={newItem.code} onChange={(e) => handleChange(setNewItem, 'code', e.target.value.toUpperCase())} /></FormControl>
                            <FormControl><FormLabel>Description</FormLabel><Textarea value={newItem.description} onChange={(e) => handleChange(setNewItem, 'description', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Sort Order</FormLabel><Input type="number" value={newItem.sortOrder} onChange={(e) => handleChange(setNewItem, 'sortOrder', e.target.value)} /></FormControl>
                            <Checkbox isChecked={!!newItem.isActive} onChange={(e) => handleChange(setNewItem, 'isActive', e.target.checked)}>Active</Checkbox>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onCreateClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCreate}>Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default BSSTestingTypesPage;